<template>
  <service-screen-layout
    v-if="saleDefinition.definition.cappedService || saleDefinition.definition.uncappedService"
    :parts="productParts"
  >
    <template v-slot:account>
      <div class="row">
        <div class="col-md-7">
          <AccountDetails
            :connectivitypackage="getPackage"
            :account="saleDefinition.definition.account"
            :sale="saleDefinition.sale"
            @save-sale="saveSale"
          />
        </div>
        <div v-if="saleDefinition.definition.cappedService" class="col-md-5">
          <ConnectivityStatus
            v-if="saleDefinition.definition.account.sessions.length > 0"
            v-allowed:view="['STAFF', 'CLIENT', 'RESELLER', 'SYSTEM']"
            :connectivityService="saleDefinition.definition.cappedService"
            :session="session"
          />
          <ConnectivityPackage
            v-allowed:view="['STAFF']"
            :bundle="
              saleDefinition.definition.cappedService.connectivityPackage
            "
          />
        </div>
        <div v-else class="col-md-5">
          <ConnectivityStatus
            v-if="saleDefinition.definition.account.sessions.length > 0"
            v-allowed:view="['STAFF', 'CLIENT', 'RESELLER', 'SYSTEM']"
            :connectivityService="saleDefinition.definition.uncappedService"
            :session="session"
          />
          <ConnectivityPackage
            v-allowed:view="['STAFF']"
            :bundle="
              saleDefinition.definition.uncappedService.connectivityPackage
            "
          />
        </div>
      </div>
    </template>
    <template v-slot:l2tpaccount v-if="saleDefinition.definition.l2tpAccount">
      <div class="row">
        <div class="col-md-8">
          <L2tpAccount
            v-allowed:view="['STAFF']"
            :l2tpAccount="saleDefinition.definition.l2tpAccount"
            @l2tpAccountChanged="l2tpAccountChanged"
            @l2tpIpv4NetworkSaved="saveL2tpIpv4Network"
          />
        </div>
      </div>
    </template>
    <template v-slot:ipaddresses>
      <div class="row">
        <div class="col-md-12">
          <div class="row row-spacing">
            <div class="col-md-12">
              <AllocatedIPAddresses
                :editable="false"
                :heading="'Assigned Addresses'"
                :addresses="combinedAllocatedNetworks"
              />
            </div>
          </div>
          <div class="row" v-if="canAddOptionalRoutedIPv4Address">
            <div class="col-md-12">
              <RoutedIpv4Address 
                :address="saleDefinition.definition.account.routedIPv4Network"
                :newRoutedIPv4AddressObj="saleDefinition.supportingObjs.routedIPv4Network"
                :pppoeAccountNumber="saleDefinition.definition.account.l2tpAccountNumber"
                :saleNumber="saleDefinition.sale.saleNumber"
                @reload-sale="getSaleDefinition"
              />
            </div>
        </div>
        </div>
      </div>
    </template>
    <template v-slot:usage>
    <ConnectivityUsageGraph
      :currentDailyUsageData="currentDailyUsageData"
      :graphTitle="saleDefinition.product.description"
      :usageType="usageType"
      :timePeriod="usageCaption"
      :usageYear="usageYear"
      :saleStartYear="saleStartYear"
      :usagePeriod="usagePeriod"
      :totalUsage="totalUsage"
      :saleStartPeriod="saleStartPeriod"
      :saleStartDate="saleDefinition.sale.startDate"
      @previousMonthUsage="previousMonthUsage"
      @nextMonthUsage="nextMonthUsage"
      @hourlyUsage="hourlyUsage"
      @dailyUsage="dailyUsage"
      @yearlyUsage="yearlyUsage"
      @previousYearlyUsage="previousYearlyUsage"
      @nextYearlyUsage="nextYearlyUsage"
    />
    </template>
    <template
      v-slot:allocateddata
      v-if="saleDefinition.definition.cappedService"
    >
      <div class="row row-spacing">
        <div class="col-md-12">
          <AllocatedTopups
            :heading="'Topups'"
            :showStatus="true"
            @topupAdded="topupAdded"
            :availableTopups="saleDefinition.selectableOptions.topup"
            :allocatedTopups="
              saleDefinition.definition.cappedService.allocatedTopups
            "
          />
          <AllocatedBundles
            bundleKeyName="connectivityPackageNumber"
            :heading="'Monthly Data'"
            :availableBundles="
              saleDefinition.selectableOptions.connectivityPackage
            "
            :allocatedBundles="
              saleDefinition.definition.cappedService.allocatedBundles
            "
          />
        </div>
      </div>
    </template>
    <template
      v-slot:notification-settings
      v-if="saleDefinition.definition.cappedService"
    >
      <UsageRecipients
        :usageRecipients="
          saleDefinition.definition.cappedService.usageRecipients
        "
        @recipientAdded="recipientAdded"
        @recipientRemoved="recipientRemoved"
      />
    </template>
    <template v-slot:dyndns v-if="saleDefinition.supportingObjs.dyndns">
      <div class="row">
        <div class="col-md-6">
          <DynDNS
            :domain="saleDefinition.supportingObjs.dyndnsdomain"
            :dynDns="saleDefinition.definition.dyndns"
            @dynDnsCreated="dynDnsCreated"
            @dynDnsRemoved="dynDnsRemoved"
          />
        </div>
      </div>
    </template>
    <template v-slot:sessions>
      <div class="row row-spacing">
        <div class="col-md-12 card-body card-scroll-100">
          <RadiusSessions
            v-allowed:view="['STAFF']"
            :sessions="saleDefinition.definition.account.sessions"
            cpeAddressName="Caller ID"
            cpeAddressField="callerId"
          />
        </div>
      </div>
    </template>
    <template v-slot:l2tpsessions v-if="saleDefinition.definition.l2tpAccount">
      <div class="row row-spacing">
        <div class="col-md-12 card-body card-scroll-100">
          <RadiusSessions
            :sessions="saleDefinition.definition.l2tpAccount.l2tpSessions"
            cpeAddressName="Caller ID"
            cpeAddressField="callerId"
          />
        </div>
      </div>
    </template>
    <template v-slot:line v-if="saleDefinition.definition.line">
      <form class="col-md-8" @submit.prevent="saveSale()">
        <OpenserveLine
          v-allowed:view="['STAFF', 'CLIENT', 'RESELLER', 'SYSTEM']"
          :line="saleDefinition.definition.line"
        />
      </form>
    </template>
    <template v-slot:router>
      <RouterConfig
        v-allowed:view="['STAFF']"
        :routerConfig="saleDefinition.definition.routerConfig"
        :saleNumber="saleNumber"
        @routerConfigSaved="routerConfigSavedHandler"
        @reloadSale="getSaleDefinition"
      />
    </template>
    <template v-slot:cpe v-if="saleDefinition.definition.account.cpe">
      <div class="row">
          <div class="col-md-12">
            <CpeDetails
              :cpe="saleDefinition.definition.account.cpe"
              :saleNumber="saleNumber"
              @saveSale="saveSale"
            />
          </div>
        </div>
    </template>
  </service-screen-layout>
</template>

<script>
import AccountDetails from "@/components/ObjParts/SingleConstructed/AccountDetails";
import ServiceScreenLayout from "@/layouts/ServiceScreenLayout";
import AllocatedBundles from "@/components/ObjParts/MultipleLoaded/AllocatedBundles";
import AllocatedTopups from "@/components/ObjParts/MultipleCaptured/AllocatedTopups";
import RadiusSessions from "@/components/ObjParts/MultipleLoaded/RadiusSessions";
import ConnectivityPackage from "@/components/ObjParts/SingleConstructed/ConnectivityPackage";
import ConnectivityStatus from "@/components/ObjParts/SingleConstructed/ConnectivityStatus";
import DynDNS from "@/components/ObjParts/OptionalCaptured/DynDNS";
import UsageRecipients from "@/components/ObjParts/MultipleCaptured/UsageRecipients";
import AllocatedIPAddresses from "@/components/ObjParts/MultipleCaptured/AllocatedIPAddresses";
import OpenserveLine from "@/components/ObjParts/OptionalConstructed/OpenserveLine";
import L2tpAccount from "@/components/ObjParts/SingleCaptured/L2tpAccount";
import ConnectivityUsageGraph from "@/components/Graphs/ConnectivityUsageGraph.vue";
import RouterConfig from "@/components/ObjParts/OptionalCaptured/RouterConfig";
import CpeDetails from "@/components/ObjParts/SingleConstructed/CpeDetails";

import RoutedIpv4Address from "@/components/ObjParts/OptionalCaptured/RoutedIpv4Address.vue";

export default {
  components: {
    AccountDetails,
    ServiceScreenLayout,
    AllocatedBundles,
    AllocatedTopups,
    RadiusSessions,
    DynDNS,
    ConnectivityPackage,
    ConnectivityStatus,
    UsageRecipients,
    AllocatedIPAddresses,
    OpenserveLine,
    L2tpAccount,
    RouterConfig,
    ConnectivityUsageGraph,
    RoutedIpv4Address,
    CpeDetails
  },
  data() {
    return {
      parts: [
        {
          name: "account",
          icon: "fa fa-user-circle",
          displayName: "Account",
          permissions: ["*"],
        },
        {
          name: "l2tpaccount",
          icon: "fa fa-link",
          displayName: "L2TP Account",
          permissions: ["STAFF"],
        },
        {
          name: "ipaddresses",
          icon: "fa fa-plus-square",
          displayName: "IP Addresses",
          permissions: ["STAFF"],
        },
        {
          name: "usage",
          icon: "fa fa-area-chart",
          displayName: "Usage",
          permissions: ["*"],
        },
        {
          name: "allocateddata",
          icon: "fa fa-database",
          displayName: "Topups and Data",
          permissions: ["*"],
        },
        {
          name: "notification-settings",
          icon: "fa fa-bullhorn",
          displayName: "Notifications",
          permissions: ["*"],
        },
        {
          name: "dyndns",
          icon: "fa fa-globe",
          displayName: "Dyn DNS",
          permissions: ["STAFF"],
        },
        {
          name: "sessions",
          icon: "fa fa-clock-o",
          displayName: "Sessions",
          permissions: ["STAFF"],
        },
        {
          name: "l2tpsessions",
          icon: "fa fa-clock-o",
          displayName: "L2TP Sessions",
          permissions: ["STAFF"],
        },
        {
          name: "line",
          icon: "fa fa-link",
          displayName: "Line",
          permissions: ["*"],
        },
        {
          name: "router",
          icon: "fa fa-cogs",
          displayName: "Router",
          permissions: ["STAFF"],
        },
        {
          name: "cpe",
          icon: "fa fa-cogs",
          displayName: "CPE",
          permissions: ["STAFF"],
        }
      ],
      saleDefinition: {
        product: {
          classificationProvider: "",
        },
        sale: {},
        definition: {
          account: {
            sessions: [],
          },
          dyndns: {},
          routerConfig: {},
        },
        selectableOptions: {
          connectivityPackage: [],
          topup: [],
        },
        supportingEnums: {
          account: {
            cappedType: [],
          },
          allocatedIPv4Addresses: {
            family: [],
          },
          allocatedIPv6Addresses: {
            family: [],
          },
        },
        supportingObjs: {
          dyndns: {},
          dyndnsdomain: {},
          routerConfig: {},
        },
      },
      saleNumber: null,
      newUsageRecipient: "",
      usagePeriod: "",
      usageYear: "",
      usageCaption: "",
      totalUsage: 0,
      currentDailyUsageData: [],
      saleStartPeriod: null,
      usageType: "",
      saleStartYear: null,
    };
  },
  computed: {
    productParts() {
      if (
        !Object.keys(this.saleDefinition.definition).includes("l2tpAccount")
      ) {
        this.parts = this.parts.filter((p) => !p.name.includes("l2tp"));
      }
      if (!this.saleDefinition.supportingObjs.dyndns) {
        this.parts = this.parts.filter((p) => !p.name.includes("dyndns"));
      }
      if (Object.keys(this.saleDefinition.definition.line).length == 0) {
        this.parts = this.parts.filter((p) => !p.name.includes("line"));
      }
      if (!this.saleDefinition.definition.cappedService) {
        this.parts = this.parts.filter(
          (p) => p.name != "allocateddata" && p.name != "notification-settings"
        );
      }

      return this.parts;
    },
    combinedAllocatedNetworks() {
      return [
        this.saleDefinition.definition.account.ipv4Networks,
        this.saleDefinition.definition.account.ipv6Networks,
      ];
    },
    getPackage() {
      return this.saleDefinition.definition.cappedService
        ? this.saleDefinition.definition.cappedService.connectivityPackage
        : this.saleDefinition.definition.uncappedService.connectivityPackage;
    },
    session() {
      return this.saleDefinition.definition.account.sessions.reduce((a, b) =>
        a.lastUpdate > b.lastUpdate ? a : b
      );
    },
    canAddOptionalRoutedIPv4Address() {
      const allowedProducts = ["OpenserveUncappedFtth"];
      return allowedProducts.includes(this.saleDefinition.product.productDefinition);
    }
  },
  mounted() {
    this.saleNumber = this.$route.params.saleNumber;
    this.usagePeriod = this.getCurrentPeriod();
    this.usageYear = ("" + this.getCurrentPeriod()).substring(0, 4);
    this.getSaleDefinition();
  },
  methods: {
    getSaleDefinition: function() {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      this.$http.get(`${hostUrl}sales/definition/sale/${saleNumber}`).then(
        (response) => {
          this.saleDefinition = response.data;

          this.saleStartPeriod = this.dateToPeriod(this.saleDefinition.sale.startDate);
          this.saleStartYear = this.saleStartPeriod.substring(0, 4); 
          this.saleDefinition.definition.account.fullUsername = `${this.saleDefinition.definition.account.username}@${this.saleDefinition.definition.account.realm.name}`;
          this.translateCurrentDailyUsageData(this.saleDefinition.definition.account.currentDailyUsage);
          this.getCurrentMonthDataUsage();
        },
        (error) => {
          console.error(error);
          this.showError("Error Fetching Sale Definition", error);
        }
      )
    },
    getCurrentMonthDataUsage: function() {
      if (this.usagePeriod === this.getCurrentPeriod()) {        
        const hostUrl = this.$config.aimsAPI;
        const l2tpAccountNumber = this.saleDefinition.definition.account.l2tpAccountNumber;
        const usagePeriod = this.usagePeriod;

        this.$http.get(`${hostUrl}connectivityaccounts/openserve/${l2tpAccountNumber}/usage/${usagePeriod}/daily`).then(
            (response) => {                            
              this.translateCurrentDailyUsageData(response.data);
            },
            (error) => {
              console.error(error);
              this.showError("Error Fetching Usage", error);
            }
          );
      }
    },
    saveSale: function() {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$http.put(`${hostUrl}sales/definition/${saleNumber}/update`, this.saleDefinition).then(
              (response) => {
                this.showSuccess("Save Success");
                this.saleDefinition = response.data;
                this.saleDefinition.definition.account.fullUsername = `${this.saleDefinition.definition.account.username}@${this.saleDefinition.definition.account.realm.name}`;
              },
              (error) => {
                console.error(error);
                this.showError("Error Saving Openserve Account", error);
              }
            ).finally(() => {
              this.getSaleDefinition();
            });;
        }
      });
    },
    dynDnsCreated: function(dyndns) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      if (dyndns.dynDNSNumber == 0) {
        this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/create/`, dyndns).then(
            (response) => {
              this.showSuccess("Dyn DNS successfully created");
              this.saleDefinition.definition.dyndns = response.data;
            },
            (error) => {
              console.error(error);
              this.showError("Error Creating Dyn DNS", error);
            }
          );
      }
    },
    dynDnsRemoved: function(dyndns) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/delete/`, dyndns).then(
          (response) => {
            this.showSuccess("Dyn DNS successfully removed");
            this.saleDefinition.definition.dyndns = {};
          },
          (error) => {
            console.error(error);
            this.showError("Error Removing Dyn DNS", error);
          }
        );
    },
    routerConfigSavedHandler: function(routerConfig) {
      this.saleDefinition.definition.routerConfig = routerConfig;
      this.saveSale();
    },
    l2tpAccountChanged: function(l2tpAccount) {
      this.saleDefinition.definition.l2tpAccount = l2tpAccount;
    },
    saveL2tpIpv4Network: function(ipv4Network) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      this.$http.put(`${hostUrl}sales/definition/${saleNumber}/obj/update/`, ipv4Network).then(
          (response) => {
            this.saleDefinition.definition.l2tpAccount.allocatedIpv4Network = response.data;
            this.showSuccess("Ipv4 Network updated successfully");
          },
          (error) => {
            console.error(error);
            this.showError("Error Updating Ipv4 Network", error);
          }
        );
    },
    topupAdded: function(topupAdded) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;
      const accountTopup = {
        class: "za.co.adept.aims.connectivity.ConnectivityTopupAllocation",
        userReadOnly: false,
      };
      accountTopup.connectivityTopupNumber = topupAdded.objKey;
      accountTopup.cappedConnectivityServiceNumber = this.saleDefinition.definition.cappedService.cappedConnectivityServiceNumber;

      this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/create/`, accountTopup).then(
          (response) => {
            response.data.isNew = true;
            response.data.topup = this.deepCopyObject(topupAdded);
            this.saleDefinition.definition.cappedService.allocatedTopups.unshift(response.data);
            this.saleDefinition.definition.cappedService.currentlyCapped = false;
            this.showSuccess("Topup successfully added");
          },
          (error) => {
            console.error(error);
            this.showError("Error Adding Topup", error);
          }
        );
    },
    recipientAdded: function(recipientNumber) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      const newUsageRecipient = {};
      newUsageRecipient.class = "za.co.adept.aims.connectivity.CappedServiceUsageRecipient";
      newUsageRecipient.cappedConnectivityServiceNumber = this.saleDefinition.definition.cappedService.cappedConnectivityServiceNumber;
      newUsageRecipient.recipient = recipientNumber;

      this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/create/`, newUsageRecipient).then(
          (response) => {
            this.saleDefinition.definition.cappedService.usageRecipients.push(response.data);
          },
          (error) => {
            console.error(error);
            this.showError("Error Adding Usage Recipient", error);
          }
        );
    },
    recipientRemoved: function(usageRecipient) {
      const hostUrl = this.$config.aimsAPI;
      const saleNumber = this.saleNumber;

      this.$http.post(`${hostUrl}sales/definition/${saleNumber}/obj/delete/`, usageRecipient).then(
          (response) => {
            const usageRecipients = this.saleDefinition.definition.cappedService.usageRecipients;
            const index = usageRecipients.findIndex((u) => u.usageRecipientNumber === usageRecipient.usageRecipientNumber);
            this.saleDefinition.definition.cappedService.usageRecipients.splice(index, 1);
          },
          (error) => {
            console.error(error);
            this.showError("Error Removing Usage Recipient", error);
          }
        );
    },
    hourlyUsage(day) {     
      const lastDay = new Date(new Date(this.usageCaption).getFullYear(), new Date(this.usageCaption).getMonth() + 1, 0).getDate();
      if (day == "previous") {
        day = parseInt(this.usageCaption.split(" ")[0]) - 1;
      } else if (day == "next") {
        day = parseInt(this.usageCaption.split(" ")[0]) + 1;
      }
      if (day < 1) {
        day = new Date(new Date(this.usageCaption).getFullYear(), new Date(this.usageCaption).getMonth(), 0).getDate();
        if (("" + this.usagePeriod).substring(4, 6) == "01") {
          this.usagePeriod = this.usagePeriod - 100 + 11;
          this.usageYear--;
        } else {
          this.usagePeriod--;
        }
      } else if (day > lastDay) {
        day = 1;
        if (("" + this.usagePeriod).substring(4, 6) == "01") {
          this.usagePeriod = this.usagePeriod - 100 + 11;
          this.usageYear++;
        } else {
          this.usagePeriod++;
        }
      }

      this.$http.get(this.$config.aimsAPI + "connectivityaccounts/openserve/" + this.saleDefinition.definition.account.l2tpAccountNumber + "/usage/" + this.usagePeriod + "/day/" + day + "/hourly/").then(
          (response) => {
            this.translateCurrentHourlyUsageData(response.data, day);
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Usage", error);
          }
        );
    },
    translateCurrentHourlyUsageData: function(usageData, day) {
      this.currentDailyUsageData = [];
      this.totalUsage = 0;
      this.usageCaption = day + " " + this.prettifyBillingPeriod(this.usagePeriod);
      this.usageType = "hourly";
      usageData.map((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        const translatedDataUsageHour = {
          label: c.hour,
          downloaded: c.outBytes,
          uploaded: c.inBytes,
        };
        this.currentDailyUsageData.push(translatedDataUsageHour);
      });
    },
    dailyUsage: function(month, year) {
      const hostUrl = this.$config.aimsAPI;
      const l2tpAccountNumber = this.saleDefinition.definition.account.l2tpAccountNumber;
      const startPeriod =  parseInt(this.saleStartPeriod);   

      this.usagePeriod = this.usagePeriod.toString();
      this.usagePeriod = `${year}${month}`;
      this.usagePeriod = parseInt(this.usagePeriod);
      
      if (this.usagePeriod < startPeriod) {        
        this.usagePeriod = startPeriod;
      } 
      
      if (this.usagePeriod > parseInt(this.getCurrentPeriod())) {
        this.usagePeriod = parseInt(this.getCurrentPeriod());
      }
      
      this.$http.get(`${hostUrl}connectivityaccounts/openserve/${l2tpAccountNumber}/usage/${this.usagePeriod}/daily/`).then(
          (response) => {
            this.translateCurrentDailyUsageData(response.data);
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Usage", error);
          }
        );
    },
    translateCurrentDailyUsageData: function(usageData) {
      this.currentDailyUsageData = {};
      this.totalUsage = 0;
      this.usageCaption = "" + this.prettifyBillingPeriod(this.usagePeriod);
      this.usageType = "daily";
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      usageData = usageData.map((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        return {
          label: c.day + " " + months[c.month - 1],
          downloaded: c.outBytes,
          uploaded: c.inBytes,
        };
      });
      this.currentDailyUsageData = {
        tooltip: "Click for hourly usage",
        data: usageData,
      };
    },
    previousMonthUsage: function() {
      const hostUrl = this.$config.aimsAPI;
      const l2tpAccountNumber = this.saleDefinition.definition.account.l2tpAccountNumber;
      const usageMonth = this.usagePeriod.toString().substring(4, 6);      

      if (usageMonth === "01") {
        this.usagePeriod = this.usagePeriod - 100 + 11;
        this.usageYear--;
      } else {
        this.usagePeriod--;
      }

      this.$http.get(`${hostUrl}connectivityaccounts/openserve/${l2tpAccountNumber}/usage/${this.usagePeriod}/daily/`).then(
          (response) => {
            this.translateCurrentDailyUsageData(response.data);
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Usage", error);
          }
        );
    },
    nextMonthUsage: function() {
      const hostUrl = this.$config.aimsAPI;
      const l2tpAccountNumber = this.saleDefinition.definition.account.l2tpAccountNumber;
      const usageMonth = this.usagePeriod.toString().substring(4, 6);      

      if (this.usagePeriod < this.getCurrentPeriod()) {
        if (usageMonth === "12") {
          this.usagePeriod = this.usagePeriod + 100 - 11;
          this.usageYear++;
        } else {
          this.usagePeriod++;
        }

        this.$http.get(`${hostUrl}connectivityaccounts/openserve/${l2tpAccountNumber}/usage/${this.usagePeriod}/daily/`).then(
            (response) => {
              this.translateCurrentDailyUsageData(response.data);
            },
            (error) => {
              console.error(error);
              this.showError("Error Fetching Usage", error);
            }
          );
      }
    },
    yearlyUsage: function() {
      const hostUrl = this.$config.aimsAPI;
      const l2tpAccountNumber = this.saleDefinition.definition.account.l2tpAccountNumber;
      const qryPeriod = `${this.usageYear}12`;

      this.usageCaption = this.usageYear;

      this.$http.get(`${hostUrl}connectivityaccounts/openserve/${l2tpAccountNumber}/usage/${qryPeriod}/monthly/`).then(
          (response) => {            
            this.translateMonthlyUsageData(response.data);
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Usage", error);
          }
        );
    },
    previousYearlyUsage: function() {
      this.usageYear--;      
      this.yearlyUsage();
    },
    nextYearlyUsage: function() {
      this.usageYear++;
      this.yearlyUsage();
    },
    translateMonthlyUsageData: function(usageData) {
      this.currentDailyUsageData = {};
      this.totalUsage = 0;
      this.usageType = "monthly";
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      usageData = usageData.map((c) => {
        this.totalUsage += c.inBytes + c.outBytes;
        return {
          label: months[c.month - 1],
          downloaded: c.outBytes,
          uploaded: c.inBytes,
        };
      });
      
      this.currentDailyUsageData = {
        tooltip: "Click for daily usage",
        data: usageData,
      };
    },
  },
};
</script>